// =============================================================================
// API client — thin fetch wrapper. All calls are same-origin (proxied in dev).
// =============================================================================
import type {
  Meta, SitesResponse, SiteFull, Insights, ControlTowerResponse,
  AgentChatResponse, AgentNextBestAction, ChangeLogEntry, FeedbackBody,
  VisualiseResponse, VisualiseCapabilities,
} from './types'
import { DEFAULT_PERSONA } from '../store/pageStore'

const BASE = '/api'

export class ApiError extends Error {
  status: number
  constructor(message: string, status: number) {
    super(message)
    this.status = status
  }
}

// Active actor identity (the top-bar persona). Module-level so api.ts stays free of a
// React dependency — the store's setPersona pushes the name + role here. Attached as
// X-Actor-Name / X-Actor-Role ONLY on calls that opt in via `actor:true` (the writes that
// RECORD an actor: the site edit + the feedback writes), so the change log + cell "last
// edited by" attribute the edit to the person and their role. Deliberately NOT sent on the
// AI agent POSTs (chat/nba/visualise) — those key their per-IP daily rate-limit on
// X-Actor-Name, so tagging them would collapse every 'Whole Estate' user onto one shared
// quota. Seeded from the store's default persona (no duplicate literal); no auth in this PoC.
let actorName = DEFAULT_PERSONA.name
let actorRole = DEFAULT_PERSONA.role
export function setActor(name: string, role: string) { actorName = name; actorRole = role }

async function request<T>(path: string, opts?: RequestInit & { actor?: boolean }): Promise<T> {
  const actorHeaders: Record<string, string> = opts?.actor ? { 'X-Actor-Name': actorName, 'X-Actor-Role': actorRole } : {}
  const res = await fetch(`${BASE}${path}`, {
    headers: { 'Content-Type': 'application/json', ...actorHeaders, ...opts?.headers },
    ...opts,
  })
  const data = await res.json().catch(() => ({}))
  if (!res.ok) throw new ApiError(data.error || data.message || `Request failed (${res.status})`, res.status)
  return data as T
}

export const api = {
  // Meta
  meta: () => request<Meta>('/meta'),

  // Sites
  sites: (params: Record<string, string | number | undefined>) => {
    const qs = new URLSearchParams()
    Object.entries(params).forEach(([k, v]) => { if (v != null && v !== '') qs.set(k, String(v)) })
    return request<SitesResponse>(`/sites?${qs}`)
  },
  site: (id: number) => request<SiteFull>(`/sites/${id}`),
  updateSite: (id: number, body: Record<string, unknown>) =>
    request<{ updated: number }>(`/sites/${id}`, { method: 'PUT', body: JSON.stringify(body), actor: true }),

  // Insights
  insights: () => request<Insights>('/insights'),

  // Control Tower
  controlTower: () => request<ControlTowerResponse>('/control-tower'),

  // Change log
  changeLog: (params: { siteId?: number; hours?: number; limit?: number }) => {
    const qs = new URLSearchParams()
    if (params.siteId) qs.set('siteId', String(params.siteId))
    if (params.hours) qs.set('hours', String(params.hours))
    if (params.limit) qs.set('limit', String(params.limit))
    return request<ChangeLogEntry[]>(`/change-log?${qs}`)
  },

  // AI Agent
  agentChat: (body: { message: string; history?: { role: string; content: string }[] }) =>
    request<AgentChatResponse>('/agent/chat', { method: 'POST', body: JSON.stringify(body) }),
  agentNba: (body: { siteId?: number | string }) =>
    request<AgentNextBestAction>('/agent/next-best-action', { method: 'POST', body: JSON.stringify(body) }),
  agentVisualise: (body: { prompt: string }) =>
    request<VisualiseResponse>('/agent/visualise', { method: 'POST', body: JSON.stringify(body) }),
  agentVisualiseCapabilities: () =>
    request<VisualiseCapabilities>('/agent/visualise/capabilities'),
  agentConfig: () => request<{ enabled: boolean; model: string }>('/agent/config'),

  // Feedback (actor-attributed telemetry writes — record created_by_name/role)
  feedback: (body: FeedbackBody) =>
    request<{ ok: boolean; id: number }>('/feedback', { method: 'POST', body: JSON.stringify(body), actor: true }),
  controlTowerFeedback: (body: { card_id: string; useful: boolean; reason?: string; metric?: string; severity?: string; card_title?: string }) =>
    request<{ ok: boolean }>('/control-tower/feedback', { method: 'POST', body: JSON.stringify(body), actor: true }),
}
